import React, {FC} from 'react';
import styled from 'styled-components';

import {AccountCell} from '../../../../../../../../../src/components/common/table/cells/accountCell';
import {EmptyCell} from '../../../../../../../../../src/components/common/table/cells/emptyCell';
import {TextCell} from '../../../../../../../../../src/components/common/table/cells/textCell';
import {TableColumn} from '../../../../../../../../../src/components/common/table/tableColumn';
import {
  TableRow,
  TableRowRenderOptions,
} from '../../../../../../../../../src/components/common/table/tableRow';
import {AccountModel} from '../../../../../../../../../src/models/api/accountModel';
import {ClickEventHandler} from '../../../../../../../../shared/helpers/src/react/reactHelpers';
import {VisualSizesEnum} from '../../../../../../../../shared/ui/src/styles/commonStyles';
import {renderCustomFieldCell} from '../contactsManagerCustomFields';
import {AccountsTableColumnsEnum} from './accountsTableConfig';

/*
 * Props.
 */

interface AccountsTableRowProps {
  isSelected: boolean;
  tableColumns: TableColumn<AccountsTableColumnsEnum>[];
  account?: AccountModel;
  options: TableRowRenderOptions;

  onSelectAccount: ClickEventHandler;
  openTeammateContactDetailsPopUp: (contactUid?: string) => void;
}

/*
 * Style.
 */

interface StyledTableRowProps {
  $minHeight: number;
}
const StyledTableRow = styled(TableRow)<StyledTableRowProps>`
  min-height: ${(p) => p.$minHeight}px;
`;

/*
 * Component.
 */

export const AccountsTableRow: FC<AccountsTableRowProps> = (props) => {
  const {isSelected, account, options, tableColumns, onSelectAccount, openTeammateContactDetailsPopUp} =
    props;

  if (!account) {
    return null;
  }

  const cellRenderer = (
    index: number,
    column: TableColumn<AccountsTableColumnsEnum>,
    isHovering: boolean,
    shouldShowStickyGradient: boolean,
  ) => {
    switch (column.type) {
      case AccountsTableColumnsEnum.NAME:
        return (
          <AccountCell
            key={AccountsTableColumnsEnum.NAME}
            value={account}
            settings={column.settings}
            isHovering={isHovering}
            isSelected={isSelected}
            avatarSize={VisualSizesEnum.LARGE}
            size={options.size}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      case AccountsTableColumnsEnum.DESCRIPTION:
        return (
          <TextCell
            key={AccountsTableColumnsEnum.DESCRIPTION}
            value={account.description}
            settings={column.settings}
            isHovering={isHovering}
            isSelected={isSelected}
            size={options.size}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      case AccountsTableColumnsEnum.DYNAMIC: {
        // The column id is mapped to be the custom field id
        const customFieldValue = account.customFieldAttributes?.find(
          (cf) => cf.id === Number(column.id),
        )?.value;
        return renderCustomFieldCell({
          customFieldValue,
          index,
          column,
          isHovering,
          isSelected,
          shouldShowStickyGradient,
          options,
          onTeammateSelect: (contactUid) => openTeammateContactDetailsPopUp(contactUid),
        });
      }
      default: {
        return (
          <EmptyCell
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            isSelected={isSelected}
            index={index}
          />
        );
      }
    }
  };

  return (
    <StyledTableRow
      onSelect={(event) => {
        if (event.defaultPrevented) {
          return;
        }
        onSelectAccount(event);
      }}
      renderColumn={(isHovering) =>
        tableColumns.map((column, index) =>
          cellRenderer(index, column, isHovering, Boolean(options.shouldShowStickyGradient)),
        )
      }
      hasHover
      isSelected={isSelected}
      $minHeight={options.tableRowHeight}
      shouldShowRightGradient={options.shouldShowRightGradient}
    />
  );
};
